import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { LockReset } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { authAPI } from '../../services/api';
import PasswordField from '../../components/Auth/PasswordField';
import Signature from '../../components/Signature';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await authAPI.resetPassword(token, password);
      toast.success(response.data.message || 'Mot de passe modifié');
      navigate('/', { replace: true, state: { openAuthTab: 0 } });
    } catch (err) {
      setError(err.response?.data?.message || 'Impossible de contacter le serveur. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <LockReset color="primary" sx={{ fontSize: 48 }} />
            <Typography component="h1" variant="h5" fontWeight="bold" gutterBottom>
              Nouveau mot de passe
            </Typography>
          </Box>

          {!token ? (
            <Alert severity="error">
              Ce lien est incomplet. Utilisez le lien reçu par email, ou refaites une demande.
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <PasswordField
                margin="normal"
                required
                label="Nouveau mot de passe"
                autoComplete="new-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                inputProps={{ minLength: 6 }}
                helperText="Minimum 6 caractères"
              />
              <PasswordField
                margin="normal"
                required
                label="Confirmer le mot de passe"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                error={mismatch}
                helperText={mismatch ? 'Les mots de passe ne correspondent pas' : ' '}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 2 }}
                disabled={loading || mismatch}
              >
                {loading ? <CircularProgress size={24} /> : 'Enregistrer le mot de passe'}
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 3, textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Link component={RouterLink} to="/forgot-password" variant="body2">
              Refaire une demande
            </Link>
            <Link component={RouterLink} to="/" variant="body2">
              Retour à la connexion
            </Link>
          </Box>
        </Paper>
      </Box>
      <Signature sx={{ mt: 4 }} />
    </Container>
  );
};

export default ResetPassword;
